// Traffic workspace model: interface state, utilisation grade, gaps and
// excursions over the panel's own samples. Pure; checked by traffic.check.ts.

export type TrafficPoint = { ts: string; in: number | null; out: number | null };
export type DirStats = { avg: number | null; peak: number | null; peak_at: string | null; p95: number | null; bytes: number | null };
export type TrafficStats = { samples: number; expected: number; coverage: number; first_at: string | null; in: DirStats; out: DirStats };
export type LagMember = { port_id: number; ifname: string | null; speed_bps: number | null; oper: string | null; rx_bps: number | null; tx_bps: number | null };
export type TrafficResponse = {
  device: { librenms_id: number; name: string; hostname: string; key: string; status: string };
  port: {
    port_id: number; ifname: string | null; ifalias: string | null; if_type: string | null; speed_bps: number | null;
    oper: string | null; admin: string | null; rx_bps: number | null; tx_bps: number | null;
    polled_at: string | null; poll_period_s: number; in_errors_rate: number | null; out_errors_rate: number | null;
  };
  window: string; from: string; to: string; step_s: number;
  series: TrafficPoint[];
  stats: TrafficStats;
  down_since: string | null;
  thresholds: { high_pct: number; crit_pct: number };
  lag: { members: LagMember[]; up: number; total: number; capacity_up_bps: number; capacity_total_bps: number } | null;
  compare: { series: TrafficPoint[]; stats: TrafficStats } | null;
};

/** Interface state, kept apart from traffic: UP at 0 bps is not DOWN, and neither is NO DATA. */
export type PortState = "up" | "down" | "degraded" | "stale" | "unknown";

/** Rates are stale after two missed polls plus a minute of grace. */
export const staleAfterMs = (periodS: number) => (2 * periodS + 60) * 1000;

export function portState(t: Pick<TrafficResponse, "port" | "lag">, nowMs: number): PortState {
  const p = t.port;
  if (p.oper === "down" || p.oper === "lowerLayerDown") return "down";
  if (!p.polled_at || nowMs - Date.parse(p.polled_at) > staleAfterMs(p.poll_period_s)) return p.oper ? "stale" : "unknown";
  if (p.oper !== "up") return "unknown";
  if (t.lag && t.lag.up < t.lag.total) return "degraded";
  return "up";
}

export type Grade = "normal" | "high" | "critical";

export function grade(pct: number | null, th: { high_pct: number; crit_pct: number }): Grade | null {
  if (pct === null) return null;
  if (pct >= th.crit_pct) return "critical";
  if (pct >= th.high_pct) return "high";
  return "normal";
}

/**
 * Capacity that can carry traffic now: a LAG with a member down carries only
 * its live members. The port's own speed when there is no member data.
 */
export function capacity(t: Pick<TrafficResponse, "port" | "lag">): number | null {
  if (t.lag && t.lag.capacity_up_bps > 0) return t.lag.capacity_up_bps;
  return t.port.speed_bps && t.port.speed_bps > 0 ? t.port.speed_bps : null;
}

export const pct = (bps: number | null | undefined, cap: number | null) =>
  bps === null || bps === undefined || !cap ? null : (bps / cap) * 100;

/** Contiguous runs of points: a new run starts wherever a poll is missing. */
export function segments(points: TrafficPoint[], stepS: number): TrafficPoint[][] {
  const out: TrafficPoint[][] = [];
  let cur: TrafficPoint[] = [];
  for (let i = 0; i < points.length; i++) {
    if (i > 0 && Date.parse(points[i].ts) - Date.parse(points[i - 1].ts) > stepS * 1000 * 1.5) {
      out.push(cur);
      cur = [];
    }
    cur.push(points[i]);
  }
  if (cur.length) out.push(cur);
  return out;
}

/** The periods with no telemetry, window edges included. */
export function gaps(points: TrafficPoint[], stepS: number, from: string, to: string): { from: string; to: string }[] {
  const step = stepS * 1000 * 1.5;
  const out: { from: string; to: string }[] = [];
  const times = [Date.parse(from), ...points.map((p) => Date.parse(p.ts)), Date.parse(to)];
  for (let i = 1; i < times.length; i++) {
    if (times[i] - times[i - 1] > step) out.push({ from: new Date(times[i - 1]).toISOString(), to: new Date(times[i]).toISOString() });
  }
  return out;
}

/**
 * When the current run above `limitBps` began, per direction, from the
 * newest point backwards. Null when the latest point is below it.
 */
export function aboveSince(points: TrafficPoint[], dir: "in" | "out", limitBps: number): string | null {
  let since: string | null = null;
  for (let i = points.length - 1; i >= 0; i--) {
    const v = points[i][dir];
    if (v === null || v < limitBps) break;
    since = points[i].ts;
  }
  return since;
}

/** Peak of one direction over the points, with when. */
export function peakOf(points: TrafficPoint[], dir: "in" | "out"): { v: number; ts: string } | null {
  let best: { v: number; ts: string } | null = null;
  for (const p of points) {
    const v = p[dir];
    if (v !== null && (!best || v > best.v)) best = { v, ts: p.ts };
  }
  return best;
}

/** "108.67 TB": bytes in decimal units, as LibreNMS and ISPs count them. */
export function fmtBytes(b: number | null | undefined): string {
  if (b === null || b === undefined) return "—";
  const u = ["B", "kB", "MB", "GB", "TB", "PB"];
  let i = 0;
  let v = b;
  while (v >= 1000 && i < u.length - 1) { v /= 1000; i++; }
  return `${v >= 100 ? v.toFixed(0) : v >= 10 ? v.toFixed(1) : v.toFixed(2)} ${u[i]}`;
}

export const isLag = (ifType: string | null) => ifType === "ieee8023adLag";
